import React from 'react';
import { Link } from 'react-router-dom';

const DIMENSIONS = [
  {
    color: '#ef4444',
    label: 'Composure',
    coach: 'Does their performance hold up when they are behind? Composure scores separate players who stay sharp from those who start forcing plays.',
  },
  {
    color: '#f97316',
    label: 'Tilt Resistance',
    coach: 'How quickly does a player recover after mistakes? High tilt resistance means one bad round does not become a bad half.',
  },
  {
    color: '#f59e0b',
    label: 'Decision Quality',
    coach: 'Fast decisions vs correct ones. This score tells you whether a player is reactive or calculated, and which style their mechanics actually support.',
  },
  {
    color: '#00d4ff',
    label: 'Reaction Speed',
    coach: 'Raw neural processing time across simple, choice, and go/no-go tasks. A baseline that predicts ceiling for mechanical roles.',
  },
  {
    color: '#10b981',
    label: 'Tracking Accuracy',
    coach: 'Sustained cursor control on a moving target. Directly maps to gunfight staying power and support positioning accuracy.',
  },
  {
    color: '#a855f7',
    label: 'Working Memory',
    coach: 'Spatial recall under increasing load. Strong predictors of callout retention, rotation reads, and multi-target prioritization.',
  },
];

const STEPS = [
  {
    n: '01',
    title: 'Sign up and get your scout dashboard',
    body: 'Team plan gives you the scout dashboard, unlimited shortlist, side-by-side comparison, and CSV export. Takes two minutes to set up.',
  },
  {
    n: '02',
    title: 'Send applicants the combine link',
    body: 'Share neurorank.app with your tryout pool. It is completely free for players. They pick their genre (FPS, MOBA, or General) and take the 15-minute test independently, no scheduling required.',
  },
  {
    n: '03',
    title: 'Review scores before investing tryout time',
    body: 'Filter your applicant pool by composure, tilt resistance, or any dimension. Shortlist the top candidates. Compare them side by side. Export to CSV for your coaching staff. Then run tryout sessions with the people who actually have the cognitive profile you are looking for.',
  },
];

export default function ForCoaches() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: 'inherit' }}>

      {/* ── Nav ─────────────────────────────────────────────────── */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 32px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-primary)', zIndex: 100 }}>
        <Link to="/" style={{ fontSize: 13, fontWeight: 800, letterSpacing: 4, color: 'var(--accent)', textDecoration: 'none', fontFamily: 'var(--font-mono, monospace)' }}>
          NEURORANK
        </Link>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <a href="https://neurorank.app/science" style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>Methodology</a>
          <Link to="/pricing" style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>Pricing</Link>
          <Link to="/leaderboard" style={{ fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>Leaderboard</Link>
          <Link to="/pricing" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bg-primary)', background: 'var(--accent)', padding: '8px 18px', borderRadius: 8, textDecoration: 'none' }}>
            Get Team Plan
          </Link>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 860, margin: '0 auto', padding: '96px 32px 72px', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 3, color: 'var(--accent)', background: 'var(--accent-dim)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 20, padding: '5px 14px', marginBottom: 28, fontFamily: 'var(--font-mono, monospace)' }}>
          FOR COACHES AND PROGRAM DIRECTORS
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 6vw, 62px)', fontWeight: 900, lineHeight: 1.08, margin: '0 0 24px', letterSpacing: '-0.02em' }}>
          Run better tryouts.<br />
          <span style={{ color: 'var(--accent)' }}>Evaluate players objectively.</span>
        </h1>
        <p style={{ fontSize: 18, color: 'var(--text-secondary)', lineHeight: 1.7, maxWidth: 620, margin: '0 auto 40px' }}>
          Send your tryout applicants the free combine link. They take a 15-minute cognitive test. You see objective scores across 6 dimensions before scheduling a single tryout session.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/pricing" style={{ fontSize: 15, fontWeight: 800, color: 'var(--bg-primary)', background: 'var(--accent)', padding: '14px 32px', borderRadius: 10, textDecoration: 'none', letterSpacing: 0.3 }}>
            Start for $39/mo
          </Link>
          <Link to="/" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', background: 'transparent', border: '1px solid var(--border)', padding: '14px 28px', borderRadius: 10, textDecoration: 'none' }}>
            Take the combine yourself
          </Link>
        </div>
        <p style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted, #4b5563)' }}>Free for players. No downloads. Works on any game.</p>
      </section>

      {/* ── Pain ────────────────────────────────────────────────── */}
      <section style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '72px 32px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 16, letterSpacing: '-0.01em' }}>
            Your tryout process has a blind spot.
          </h2>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.7, maxWidth: 680, marginBottom: 40 }}>
            You see rank. You see VOD clips. You hear how players talk in voice chat. What you cannot see is how they respond to being down three rounds, whether they make decisions faster when tilted, or how their composure score compares to your current roster.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            {[
              { label: 'Rank is gameable', body: 'Boosted accounts and inflated ranks are common at every level. Cognitive data does not care about your rank.' },
              { label: 'Players self-report', body: 'Every applicant says they are hardworking, coachable, and have good game sense. The combine measures it instead.' },
              { label: 'Tryouts miss mental performance', body: 'A three-day tryout session shows mechanics under low pressure. It rarely predicts composure in a match-deciding round.' },
            ].map(item => (
              <div key={item.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 22px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{item.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{item.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────── */}
      <section style={{ maxWidth: 860, margin: '0 auto', padding: '80px 32px' }}>
        <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 3, color: 'var(--accent)', fontFamily: 'var(--font-mono, monospace)', marginBottom: 20 }}>
          HOW IT WORKS
        </div>
        <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 48, letterSpacing: '-0.01em' }}>
          Three steps to a data-driven tryout.
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {STEPS.map((step, i) => (
            <div key={step.n} style={{ display: 'flex', gap: 28, paddingBottom: i < STEPS.length - 1 ? 40 : 0, borderLeft: i < STEPS.length - 1 ? '1px solid var(--border)' : '1px solid transparent', marginLeft: 19, paddingLeft: 32, position: 'relative' }}>
              <div style={{ position: 'absolute', left: -19, top: 0, width: 38, height: 38, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: 'var(--bg-primary)', flexShrink: 0, fontFamily: 'var(--font-mono, monospace)' }}>
                {step.n}
              </div>
              <div style={{ paddingTop: 6, paddingBottom: i < STEPS.length - 1 ? 40 : 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{step.title}</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7, maxWidth: 560 }}>{step.body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Dimensions ──────────────────────────────────────────── */}
      <section style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', padding: '80px 32px' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 3, color: 'var(--accent)', fontFamily: 'var(--font-mono, monospace)', marginBottom: 20 }}>
            WHAT YOU MEASURE
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 12, letterSpacing: '-0.01em' }}>
            Six dimensions. Each tells you something rank cannot.
          </h2>
          <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.7, maxWidth: 600, marginBottom: 44 }}>
            Scores are percentile-ranked against all players in the database. You see exactly where each applicant sits relative to the competitive player population.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {DIMENSIONS.map(dim => (
              <div key={dim.label} style={{ background: 'var(--bg-card)', border: `1px solid ${dim.color}33`, borderRadius: 12, padding: '20px 22px', borderTop: `3px solid ${dim.color}` }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: dim.color, marginBottom: 10, letterSpacing: 0.5 }}>{dim.label}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65 }}>{dim.coach}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Social proof / data ─────────────────────────────────── */}
      <section style={{ maxWidth: 860, margin: '0 auto', padding: '80px 32px', textAlign: 'center' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 24, marginBottom: 56 }}>
          {[
            { val: '15 min', label: 'Average combine time' },
            { val: 'Free', label: 'Cost for players' },
            { val: '6', label: 'Dimensions measured' },
            { val: 'Any game', label: 'FPS, MOBA, or General' },
          ].map(stat => (
            <div key={stat.label} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '24px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--accent)', marginBottom: 6 }}>{stat.val}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', letterSpacing: 0.3 }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Profile example callout */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: '28px 32px', marginBottom: 56, textAlign: 'left', maxWidth: 560, margin: '0 auto 56px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: 'var(--accent)', fontFamily: 'var(--font-mono, monospace)', marginBottom: 14 }}>EXAMPLE APPLICANT PROFILE</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Adaptive Strategist</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>Overall: 74th percentile</div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted, #4b5563)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 12px' }}>FPS</div>
          </div>
          {[
            { label: 'Composure', val: 81, color: '#ef4444' },
            { label: 'Tilt Resistance', val: 76, color: '#f97316' },
            { label: 'Decision Quality', val: 68, color: '#f59e0b' },
            { label: 'Reaction Speed', val: 71, color: '#00d4ff' },
          ].map(d => (
            <div key={d.label} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: d.color }}>{d.val}th</span>
              </div>
              <div style={{ height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${d.val}%`, background: d.color, borderRadius: 2, opacity: 0.8 }} />
              </div>
            </div>
          ))}
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted, #4b5563)', lineHeight: 1.6, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            81st percentile composure with 76th percentile tilt resistance. Performs better under pressure than their reaction speed suggests. High-upside pick for a structured system.
          </div>
        </div>
      </section>

      {/* ── Pricing CTA ─────────────────────────────────────────── */}
      <section style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)', padding: '80px 32px' }}>
        <div style={{ maxWidth: 620, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 3, color: 'var(--accent)', fontFamily: 'var(--font-mono, monospace)', marginBottom: 20 }}>
            TEAM PLAN
          </div>
          <h2 style={{ fontSize: 36, fontWeight: 900, marginBottom: 16, letterSpacing: '-0.015em' }}>
            $39/month.<br />No per-seat fees.
          </h2>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 36 }}>
            One plan covers your entire coaching staff. Players always take the test for free. You get the scout dashboard, unlimited shortlisting, side-by-side comparison, CSV export, and saved filter presets.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', marginBottom: 32 }}>
            {[
              'Scout dashboard with advanced filters',
              'Unlimited shortlist (no cap on candidates)',
              'Compare up to 4 profiles side by side',
              'CSV export for coaching staff review',
              'Saved filter presets for recurring tryout criteria',
              'Players always take the combine for free',
            ].map(f => (
              <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'var(--text-secondary)' }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none"><path d="M1.5 4L3.5 6L6.5 2" stroke="#00d4ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                {f}
              </div>
            ))}
          </div>
          <Link to="/pricing" style={{ display: 'inline-block', fontSize: 16, fontWeight: 800, color: 'var(--bg-primary)', background: 'var(--accent)', padding: '15px 40px', borderRadius: 10, textDecoration: 'none', letterSpacing: 0.3 }}>
            Start Team Plan
          </Link>
          <p style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted, #4b5563)' }}>Cancel any time. No lock-in.</p>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section style={{ maxWidth: 700, margin: '0 auto', padding: '80px 32px' }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 36, letterSpacing: '-0.01em' }}>Common questions</h2>
        {[
          {
            q: 'Do players need to pay to take the combine?',
            a: 'No. The combine is completely free for players. You send them a link, they take the test, you see the results. Zero friction for your applicant pool.',
          },
          {
            q: 'Is this game-specific?',
            a: 'Players choose their genre: FPS, MOBA, or General. The FPS combine swaps the working memory module for a flicker detection test. The MOBA version uses a target sequencing module instead of aim precision. All versions produce the same six dimension scores so you can compare across game backgrounds.',
          },
          {
            q: 'Can this replace our tryout process?',
            a: 'No, and it is not designed to. It is a filter layer before you invest tryout session time. Use it to identify who is worth scheduling, then run your normal process with a better-qualified pool.',
          },
          {
            q: 'How do the scores compare across players?',
            a: 'Scores are percentile-ranked against all players in the database. 50th percentile is average for competitive players who seek out a cognitive test, which is already above the general gaming population.',
          },
          {
            q: 'What about cheating the test?',
            a: 'The combine is hard to deliberately cheat because the tasks are time-sensitive and counterintuitive to game. Most players trying to game the composure test end up overcompensating and scoring lower. The tilt module specifically tracks betting behavior under stress, which is difficult to fake consistently.',
          },
        ].map((item, i, arr) => (
          <div key={item.q} style={{ paddingBottom: i < arr.length - 1 ? 28 : 0, marginBottom: i < arr.length - 1 ? 28 : 0, borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{item.q}</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item.a}</div>
          </div>
        ))}
      </section>

      {/* ── Footer CTA ──────────────────────────────────────────── */}
      <section style={{ borderTop: '1px solid var(--border)', padding: '60px 32px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>Ready to run your next tryout cycle with data?</h2>
        <p style={{ fontSize: 15, color: 'var(--text-secondary)', marginBottom: 28 }}>Start today. Your applicants take it for free.</p>
        <Link to="/pricing" style={{ display: 'inline-block', fontSize: 15, fontWeight: 800, color: 'var(--bg-primary)', background: 'var(--accent)', padding: '14px 36px', borderRadius: 10, textDecoration: 'none' }}>
          Get Team Plan
        </Link>
      </section>

    </div>
  );
}
